import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { selectContractDetail } from 'src/store/selectors/contractSelectors';
import selectFormatDate from 'src/utils/selectDateFormat';
import {
  getContractFinancialTermsFieldConfig,
  CONTRACT_FINANCIAL_TERMS_IMPACT_FIELDS
} from 'src/utils/constants/contracts';
import useStyles from './fieldUpdateDetailStyles';

const isValueEmpty = (fieldConfig, value) => {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  return fieldConfig?.isEmpty ? fieldConfig.isEmpty(value) : false;
};

const formatChangeValue = (fieldConfig, value, t) => {
  if (isValueEmpty(fieldConfig, value)) {
    return t('COMMON.NO_DATA');
  }
  return fieldConfig ? fieldConfig.format(value) : String(value);
};

/**
 * Who changed a contract's financial terms, why, and exactly which fields
 * moved — the audit detail behind a FIELD_UPDATE event (MET-100089). Mirrors
 * SettlementComparisonDialog's structure (header/content/footer) so the two
 * event-detail dialogs in this table read as the same family.
 */
const FieldUpdateDetailDialog = ({
  open,
  onClose,
  description,
  user,
  changes,
  eventDate
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const contract = useSelector(selectContractDetail);

  if (!open) {
    return null;
  }

  // Built only once the dialog is actually rendering — this is one of
  // potentially many event rows in the table, most of which never open.
  const fieldConfig = getContractFinancialTermsFieldConfig(t);
  const changeList = changes || [];
  const hasFinancialImpact = changeList.some(change =>
    CONTRACT_FINANCIAL_TERMS_IMPACT_FIELDS.includes(change.field)
  );

  const subtitle = t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.SUBTITLE', {
    folio: contract?.folio ?? ''
  });

  return (
    <Dialog
      id="field-update-detail"
      classes={{ paper: classes.paper }}
      open={open}
      onClose={onClose}
      scroll="body"
      fullWidth
      aria-labelledby="field-update-detail-title"
    >
      <DialogTitle className={classes.header} component="div">
        <Box>
          <Typography variant="overline" className={classes.eyebrow}>
            {t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.EYEBROW')}
          </Typography>
          <Typography
            variant="h3"
            className={classes.title}
            id="field-update-detail-title"
          >
            {t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.TITLE')}
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            {subtitle}
          </Typography>
        </Box>
        <IconButton
          aria-label={t('COMMON.CLOSE')}
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Box className={classes.summary}>
          <Box className={classes.summaryCard}>
            <Typography variant="overline" className={classes.summaryLabel}>
              {t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.CONTRACT_CARD_LABEL')}
            </Typography>
            <Typography variant="body1" className={classes.summaryValue}>
              {contract?.folio ?? t('COMMON.NO_DATA')}
            </Typography>
          </Box>
          <Box className={classes.summaryCard}>
            <Typography variant="overline" className={classes.summaryLabel}>
              {t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.MADE_BY')}
            </Typography>
            <Typography variant="body1" className={classes.summaryValue}>
              {user?.name || t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.NO_USER')}
            </Typography>
          </Box>
          <Box className={classes.summaryCard}>
            <Typography variant="overline" className={classes.summaryLabel}>
              {t(
                'CONTRACT_VIEW.EVENTS.FIELD_UPDATE.REGISTERED_DATE_CARD_LABEL'
              )}
            </Typography>
            <Typography variant="body1" className={classes.summaryValue}>
              {selectFormatDate(eventDate)}
            </Typography>
          </Box>
        </Box>

        <Box className={classes.reasonCard}>
          <Typography variant="overline" className={classes.reasonLabel}>
            {t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.REASON')}
          </Typography>
          <Typography variant="body2" className={classes.reasonText}>
            {description}
          </Typography>
        </Box>

        <Box className={classes.changesCard}>
          <Box className={classes.changesHeader}>
            <Typography variant="overline" className={classes.changesTitle}>
              {t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.CHANGES_TITLE')}
            </Typography>
            {hasFinancialImpact && (
              <Typography
                component="span"
                className={classes.financialImpactBadge}
              >
                {t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.FINANCIAL_IMPACT_BADGE')}
              </Typography>
            )}
          </Box>
          <Box className={classes.rows}>
            {changeList.map(change => {
              const config = fieldConfig[change.field];
              return (
                <Box className={classes.row} key={change.field}>
                  <Typography variant="body2" className={classes.rowLabel}>
                    {config?.label || change.field}
                  </Typography>
                  <Box className={classes.rowValues}>
                    <Box
                      className={`${classes.valueBox} ${classes.oldValueBox}`}
                    >
                      <Typography
                        variant="caption"
                        className={classes.valueCaption}
                      >
                        {t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.OLD_VALUE')}
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.oldValue}
                      >
                        {formatChangeValue(config, change.oldValue, t)}
                      </Typography>
                    </Box>
                    <Box className={classes.arrowCircle}>
                      <ArrowForwardIcon fontSize="small" />
                    </Box>
                    <Box
                      className={`${classes.valueBox} ${classes.newValueBox}`}
                    >
                      <Typography
                        variant="caption"
                        className={classes.valueCaption}
                      >
                        {t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.NEW_VALUE')}
                      </Typography>
                      <Typography
                        variant="body2"
                        className={classes.newValue}
                      >
                        {formatChangeValue(config, change.newValue, t)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box className={classes.operationalReading}>
          <InfoOutlinedIcon
            fontSize="small"
            className={classes.operationalReadingIcon}
          />
          <Box>
            <Typography
              variant="body2"
              className={classes.operationalReadingTitle}
            >
              {t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.OPERATIONAL_READING_TITLE')}
            </Typography>
            <Typography variant="body2">
              {t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.OPERATIONAL_READING_BODY')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <Box className={classes.actions}>
        <Typography variant="body2" className={classes.registered}>
          {t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.REGISTERED_ON', {
            date: selectFormatDate(eventDate)
          })}
        </Typography>
        <Button variant="contained" color="primary" onClick={onClose}>
          {t('CONTRACT_VIEW.EVENTS.FIELD_UPDATE.UNDERSTOOD')}
        </Button>
      </Box>
    </Dialog>
  );
};

FieldUpdateDetailDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  eventDate: PropTypes.string,
  description: PropTypes.string,
  user: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string
  }),
  changes: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      oldValue: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool
      ]),
      newValue: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool
      ])
    })
  )
};

export default FieldUpdateDetailDialog;
